/*
 * WHAT A TOOL IS CALLED, TO A PERSON.
 *
 * A model needs `ai_admin_delete_memory`: it is an identifier, providers constrain the character
 * set, and it is the key everything routes on. A human reading a permissions screen, or watching a
 * chat say what the AI is doing right now, needs "Delete Memory".
 *
 * ONE FUNCTION, shared, because the two surfaces have to agree. A config screen that calls it
 * "Delete Memory" while the chat says `ai_admin_delete_memory` teaches the user that they are two
 * different things. Derived rather than declared so a tool a UI ships this afternoon reads properly
 * without anyone writing a label for it, which is the same property the whole dynamic catalog rests
 * on.
 *
 * The RAW NAME IS NEVER HIDDEN, only demoted: every surface that shows a title also has the id
 * available for the tooltip, because the id is what an admin writes a rule against and what appears
 * in an audit row.
 */
/** Words that should keep their casing rather than being Title Cased. */
const ACRONYMS = new Set(['ai', 'api', 'ui', 'url', 'id', 'plc', 'scxml', 'mcp', 'json', 'csv', 'sql', 'io']);
const titleCase = (word) => ACRONYMS.has(word.toLowerCase()) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1);
/** `deleteMemory` / `delete_memory` / `delete-memory` → ['delete', 'Memory'] */
function words(s) {
    return s
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase boundaries
        .split(/[\s._-]+/)
        .filter(Boolean);
}
/**
 * Every tool name on this platform, in one of three shapes:
 *
 *   `ai_admin_delete_memory`   a server RPC, service-prefixed
 *   `ui.write.setSymbol`       declared by a UI, dot-namespaced
 *   `mcp__notion__search`      an MCP server's tool
 *
 * The prefix carries WHERE it runs, which the permissions tree already shows as a group, so it is
 * dropped from the title rather than repeated on every row inside that group.
 */
/** The server tool that parks a run until later (`AiAdmin.WaitFor`). Named once, because the row that
 *  draws a wait and the control that cancels one must agree on which calls are waits. */
export const WAIT_TOOL = 'ai_admin_wait_for';
/** Every tool that parks a run: until a time, until a value, until a state machine reaches a state.
 *  They render and cancel alike — only what ends them differs. */
export const WAIT_TOOLS = [WAIT_TOOL, 'ai_admin_wait_for_value', 'ai_admin_wait_for_model_state'];
export const isWaitTool = (name) => WAIT_TOOLS.includes(name);
export function toolDisplayName(name) {
    const raw = name.trim();
    if (!raw)
        return '';
    // An MCP tool: the server is the group, so only the tool's own name is the title.
    const mcp = /^mcp__([^_]+(?:_[^_]+)*?)__(.+)$/.exec(raw);
    if (mcp)
        return words(mcp[2]).map(titleCase).join(' ');
    // A UI tool: `ui.write.setSymbol` → "Set Symbol". The namespace is the group.
    if (raw.includes('.')) {
        const last = raw.slice(raw.lastIndexOf('.') + 1);
        return words(last).map(titleCase).join(' ');
    }
    /*
     * A server RPC, named `<service>_<method>` snake-cased. The service prefix is dropped because
     * every server tool shares it, so it is noise on every row.
     *
     * Matched rather than assumed to be two tokens: `ai_admin` is one service whose name contains an
     * underscore, and splitting on the first would leave "Admin Delete Memory".
     */
    const stripped = raw.replace(/^ai_admin_|^ai_/, '');
    return words(stripped).map(titleCase).join(' ');
}
/**
 * The group a tool belongs to, as a person reads it.
 *
 * PRODUCTS ARE NAMED, not derived. `ui:fleet` deriving to "Fleet app" was a machine's guess at a
 * name the company already has: these are Vitesse App, Vitesse AI Console and Vitesse Fleet Manager,
 * and a governance screen listing them by their internal surface ids reads as an internal tool.
 * A surface we do not recognise still gets a readable fallback, because a new UI must show up in the
 * permissions tree the day it ships rather than the day someone adds it here.
 */
const SURFACE_NAMES = {
    app: 'Vitesse App',
    ai: 'Vitesse AI Console',
    fleet: 'Vitesse Fleet Manager',
};
export function toolGroupName(origin) {
    // What the AI can do regardless of which app is open: read the screen, search the web, run code,
    // spawn a sub-agent. Not a product, which is why it is not named like one.
    if (origin === 'platform')
        return 'Vitesse Platform';
    // The AI console's own admin surface: usage, conversations, memory. It IS the AI Console's tools,
    // and grouping them anywhere else made the console look like it had none.
    if (origin === 'ai')
        return 'Vitesse AI Console';
    const [kind, ...rest] = origin.split(':');
    const name = rest.join(':');
    if (!name)
        return titleCase(origin);
    if (kind === 'mcp')
        return words(name).map(titleCase).join(' ');
    return SURFACE_NAMES[name] ?? `${words(name).map(titleCase).join(' ')} app`;
}
//# sourceMappingURL=toolname.js.map